import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Collapse, IconButton, Typography, Box } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { getTimeZone } from '../../../../../stores/SessionStore';
import useStyles from './VerifyResultStyles';
import { useExpandToggle } from './UseExpandToggle';

const LOG_VIEWER_ITEM_ID = 'log-viewer';

// Matches timestamps like "2026-05-27 07:38:17,673"
// portal/ui source: verifyScriptedTest.service.js buildLogDetail() convertTimeZone()
const LOG_TIMESTAMP_REGEX =
    /([0-9]{4}-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1]) (2[0-3]|[01][0-9]):[0-5][0-9]:[0-5][0-9],[0-9]{3})/g;
const FROM_FORMAT = 'YYYY-MM-DD HH:mm:ss,SSS';
const TO_FORMAT = 'D MMM YYYY h:mm:ss,SSS A z';

/**
 * Convert log timestamps from UTC to user timezone.
 * portal/ui source: verifyScriptedTest.service.js buildLogDetail() → convertTimeZone()
 */
function convertLogTimestamps(rawLog) {
    if (!rawLog) return '';
    const userTimeZone = getTimeZone();

    return rawLog
        .split('\n')
        .map((line) =>
            line.replace(LOG_TIMESTAMP_REGEX, (match) =>
                moment.utc(match, FROM_FORMAT).tz(userTimeZone).format(TO_FORMAT)
            )
        )
        .join('\n');
}

/**
 * Collapsible log viewer for scripted test results.
 */
function LogViewer({ logFile }) {
    const classes = useStyles();
    const { i18n } = useLingui();
    const { isExpanded, toggleExpand } = useExpandToggle();
    const expanded = isExpanded(LOG_VIEWER_ITEM_ID);

    const convertedLog = useMemo(() => convertLogTimestamps(logFile), [logFile]);

    if (!logFile) return null;

    const handleToggle = () => toggleExpand(LOG_VIEWER_ITEM_ID);

    return (
        <Box className={classes.logContainer}>
            <Box
                className={classes.logToggle}
                onClick={handleToggle}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') handleToggle();
                }}
            >
                <IconButton size="small">{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                <Typography variant="body2">{i18n._(t`Log Detail...`)}</Typography>
            </Box>
            <Collapse in={expanded}>
                <pre className={classes.logContent}>{convertedLog}</pre>
            </Collapse>
        </Box>
    );
}

LogViewer.propTypes = {
    logFile: PropTypes.string,
};

LogViewer.defaultProps = {
    logFile: null,
};

export default LogViewer;
